import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import VariantPicker from '../components/VariantPicker';
import StoryLoader from '../components/StoryLoader';
import ImageDropzone from '../components/ImageDropzone';
import ImageTips from '../components/ImageTips';
import PhotoGuide from '../components/PhotoGuide';
import BackButton from '../components/BackButton';
import TopicMedia from '../components/TopicMedia';
import { useCart } from '../state/cart';
import { useAuth } from '../state/auth';
import { useExperiment } from '../state/experiment';
import { saveLeadContact, loadLeadContact } from '../lib/leadContact';
import { getAfMode } from '../lib/afMode';
import { getVisitorId, trackEvent } from '../lib/track';
import { trackCompleteRegistration, getFbCookies } from '../lib/pixel';
import {
  FamilyStructureField,
  PersonalityTraitsField,
} from '../components/TherapeuticFields';
import { useT, useLang, useLocalField } from '../i18n';

// WhatsApp contact surfaced when a visitor hits the free-preview cap. Same
// number/link used across the site (Header, Footer, Terms).
const WHATSAPP_LINK = 'https://wa.me/972505020178';
const WHATSAPP_DISPLAY = '+972 50-502-0178';

// Mint the story's share token in the browser so we can navigate to the
// preview and start polling immediately — before the (slow) generation request
// returns. 16 random bytes → 32 hex chars, matching the server's token format.
function newShareToken() {
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Sample values applied when the URL carries ?prefill=1 — a one-click fill for
// testing the fears flow end-to-end without retyping the whole form. Tuned for
// the fears story line. familyStructure/personalityTraits use exact option
// strings so the dropdown
// and trait chips render as selected.
const PREFILL_VALUES = {
  name: 'نيل',
  age: '4',
  gender: 'ولد',
  storyLanguage: 'lahja',
  issue: 'يبكي ويختبئ ورا أمه لما يشوف كلب',
  whenHappens: 'لما نكون برّا أو عند ناس',
  howBehaves: 'يصرخ ويتمسّك فيي',
  smallGoal: 'يقدر يقترب شوي بدون خوف',
  familyStructure: 'أب وأم',
  personalityTraits: 'خجول، حسّاس',
  interests: 'سبايدر مان والرسم',
  environment: 'حيفا',
};

// Sample requester contact applied alongside PREFILL_VALUES under ?prefill=1, so
// the new pre-preview contact step doesn't block end-to-end testing of the flow.
const PREFILL_CONTACT = { name: 'سارة', email: 'test@example.com', phone: '0501234567' };

// A single topic option. Shows the generated sample image when present, and
// falls back to the emoji if the file isn't there yet (covers are generated by
// server/scripts/genCovers.js out-of-band, so the picker must work before they
// exist).
function TopicChip({ option, active, onClick }) {
  const lf = useLocalField();
  return (
    <button
      type="button"
      className={`topic-chip ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      <TopicMedia image={option.image} emoji={option.emoji} />
      <span className="topic-chip-label">{lf(option, 'labelAr')}</span>
    </button>
  );
}

// Ready-made story topics + an "Other" chip that reveals a free-text box. The
// selected value (or the typed text) is written straight into the field key
// (`issue`), which the imaginary story line consumes as the story's theme.
// When `collapsed` (the topic was chosen on the storefront and passed via the
// URL), the full grid is hidden — only the chosen topic shows, with a button to
// reopen the grid.
function TopicPicker({ field, value, other, collapsed, onSelect, onOther, onOtherChange, onEdit }) {
  const t = useT();
  const lf = useLocalField();
  const selected = (field.options || []).find((o) => o.value === value);
  if (collapsed && selected) {
    return (
      <div className="topic-picker topic-picker-collapsed">
        <div className="topic-chip active topic-chip-selected">
          <TopicMedia image={selected.image} emoji={selected.emoji} />
          <span className="topic-chip-label">{lf(selected, 'labelAr')}</span>
        </div>
        <button type="button" className="topic-change-btn" onClick={onEdit}>
          {t.topicChange}
        </button>
      </div>
    );
  }
  return (
    <div className="topic-picker">
      <div className="topic-grid">
        {(field.options || []).map((opt) => (
          <TopicChip
            key={opt.value}
            option={opt}
            active={!other && value === opt.value}
            onClick={() => onSelect(opt.value)}
          />
        ))}
        <button
          type="button"
          className={`topic-chip topic-chip-other ${other ? 'active' : ''}`}
          onClick={onOther}
        >
          <span className="topic-chip-media">
            <span className="topic-chip-emoji" aria-hidden="true">{t.topicOtherEmoji}</span>
          </span>
          <span className="topic-chip-label">{t.topicOther}</span>
        </button>
      </div>
      {other && (
        <textarea
          className="topic-other-input"
          rows={3}
          value={value}
          onChange={onOtherChange}
          placeholder={t.topicOtherPlaceholder}
          required
          autoFocus
        />
      )}
    </div>
  );
}

const TOGGLE_DISPLAY_HE = { 'ولد': 'בן', 'بنت': 'בת' };

// "5" → "5 سنوات" for the hero read-back on the contact step. Both languages
// count the noun by the number (سنة / سنتان / سنوات — שנה / שנתיים / שנים), and
// in both the 1 and 2 forms carry the count themselves, so no numeral is
// printed for them. Non-numeric input (a typo) is passed through as typed.
function formatAge(age, t) {
  const n = Number(String(age || '').replace(/\D/g, ''));
  if (!n) return String(age || '').trim();
  if (n === 1) return t.ageUnitOne;
  if (n === 2) return t.ageUnitTwo;
  return `${n} ${n <= 10 ? t.ageUnitFew : t.ageUnitMany}`;
}

// Seed the controlled form state from a template's personalization schema. Shared
// by the storefront create flow and the pay-first order-item create flow so both
// start from the same field shape.
function buildInitialValues(tp, lang) {
  const initial = {
    storyLanguage: lang === 'he' ? 'fos7a' : 'lahja',
    imageStyle: 'pixar',
    familyStructure: '',
    personalityTraits: '',
    whenHappens: '',
    howBehaves: '',
    smallGoal: '',
  };
  tp.personalizationSchema?.fields?.forEach((f) => {
    // Toggle fields (gender) start UNSELECTED on purpose. Pre-selecting the
    // first option made the field look answered, so parents of boys who never
    // touched it shipped a girl's story — the single most common wrong-book
    // cause. handleSubmit blocks submission until a required toggle is tapped.
    if (f.type === 'hidden') initial[f.key] = f.value ?? '';
    else initial[f.key] = '';
  });
  return initial;
}

// `orderItemMode` renders this same form for the pay-first flow's post-payment
// step: the customer has already paid for a book slot and is now creating its
// story from the order. In that mode the template + variant are fixed by the
// order item, the free-preview cap and the lead-capture contact step are skipped
// (they've paid and we already know them), and submission goes to the order-item
// create-story endpoint instead of the public /stories/create.
export default function Create({ orderItemMode = false }) {
  const t = useT();
  const { lang } = useLang();
  const lf = useLocalField();
  const params = useParams();
  const slug = params.slug;
  const orderId = params.id;
  const itemIndex = params.index;
  const navigate = useNavigate();
  const { refreshPendingOrders } = useCart();
  const { payFirst, ready: experimentReady } = useExperiment();

  // Pay-first variant: the storefront free-preview form is replaced by the
  // add-to-cart → pay → create-from-order flow, so bounce a pay-first visitor who
  // lands on /create/:slug (an old link or the back button) to the product page.
  // Never in order-item mode — there the customer has already paid and is meant
  // to be here creating their story.
  useEffect(() => {
    if (!orderItemMode && experimentReady && payFirst && slug) {
      navigate(`/p/${slug}`, { replace: true });
    }
  }, [orderItemMode, experimentReady, payFirst, slug, navigate]);
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [selectedSku, setSelectedSku] = useState('');
  const [photos, setPhotos] = useState([]);
  const [characterImage, setCharacterImage] = useState(null);
  const [referenceImages, setReferenceImages] = useState([]);
  // Per-reference descriptions (aligned by index with referenceImages). Tells
  // the story generator who/what each extra image is, so that character/object
  // is only drawn into scenes where it fits.
  const [referenceDescriptions, setReferenceDescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // "No photo" recommendation prompt: shown after the parent presses create
  // without a character photo or a written appearance description.
  const [showNoPhotoPrompt, setShowNoPhotoPrompt] = useState(false);
  // Opens instead of the file picker when the parent taps the empty character-
  // photo zone: the worked example first, upload inside the same modal.
  const [showPhotoHelp, setShowPhotoHelp] = useState(false);

  // While either prompt is open, lock background scroll so the modal — which is
  // portaled to <body> to escape the transformed .app-main ancestor — is the
  // sole focus and can't be scrolled past or lost behind page content.
  useEffect(() => {
    if (!showNoPhotoPrompt && !showPhotoHelp) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showNoPhotoPrompt, showPhotoHelp]);

  useEffect(() => {
    if (!showPhotoHelp) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowPhotoHelp(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPhotoHelp]);
  // When the imagination topic picker is in "Other" mode the free-text box is
  // shown and drives the topic value instead of a preset chip.
  const [topicOther, setTopicOther] = useState(false);
  // True when the topic was preselected from the storefront (?topic=…): the
  // full grid stays collapsed since the choice was already made.
  const [topicLocked, setTopicLocked] = useState(false);
  // True when the visitor arrived through an ?af=pdf link (see lib/afMode):
  // the digital (PDF) variant is pre-selected and the book options are hidden.
  const [pdfOnly, setPdfOnly] = useState(false);

  // Storefront create flow: load the template by slug and seed the form (topic
  // deep-links, ?prefill, ?af=pdf, ?variant). Skipped in order-item mode.
  useEffect(() => {
    if (orderItemMode) return;
    api.get(`/api/templates/${slug}`)
      .then((tp) => {
        setTemplate(tp);
        const initial = buildInitialValues(tp, lang);
        // Deep link from the storefront topic tiles: ?topic=<value> preselects a
        // ready-made topic, ?topic=__other__ opens the free-text box.
        const params = new URLSearchParams(window.location.search);
        const topicField = tp.personalizationSchema?.fields?.find((f) => f.type === 'topic');
        const topicParam = params.get('topic');
        if (topicField && topicParam) {
          if (topicParam === '__other__') setTopicOther(true);
          else { initial[topicField.key] = topicParam; setTopicLocked(true); }
        }
        // ?prefill=1 fills the form with sample values for quick testing.
        const prefillOn = params.get('prefill') === '1';
        setValues(prefillOn ? { ...initial, ...PREFILL_VALUES } : initial);
        if (prefillOn) setContact(PREFILL_CONTACT);
        // ?af=pdf funnel: lock the order to the digital (PDF) variant — it's
        // pre-selected and the book options never render. Falls back to the
        // regular picker when the template has no active digital variant.
        const digital = getAfMode() === 'pdf'
          ? tp.variants?.find((v) => v.isActive && v.type === 'digital')
          : null;
        if (digital) {
          setSelectedSku(digital.sku);
          setPdfOnly(true);
        } else {
          // Honor a variant chosen upstream (e.g. on the template page) and
          // passed via ?variant=<sku>, so the buyer's preview-type choice isn't
          // dropped at the handoff into the form. Falls back to the first active
          // variant when the param is absent or no longer valid.
          const variantParam = params.get('variant');
          const fromParam = variantParam
            && tp.variants?.find(v => v.isActive && v.sku === variantParam);
          const firstActive = tp.variants?.find(v => v.isActive);
          if (fromParam) setSelectedSku(fromParam.sku);
          else if (firstActive) setSelectedSku(firstActive.sku);
        }
      })
      .catch(() => setTemplate(null))
      .finally(() => setLoading(false));
  }, [slug, orderItemMode, lang]);

  // Pay-first order-item create flow: the customer paid for this book slot and is
  // now creating its story. Load the order to resolve the fixed template + variant
  // for the item, then seed the form. If the slot's story already exists, jump
  // straight to its preview instead of offering to create a second one.
  useEffect(() => {
    if (!orderItemMode) return;
    let cancelled = false;
    (async () => {
      try {
        const order = await api.get(`/api/orders/${orderId}`);
        if (cancelled) return;
        const item = order.items?.[Number(itemIndex)];
        if (!item || !item.templateId?.slug) { setTemplate(null); setLoading(false); return; }
        if (item.storyId?.shareToken) {
          navigate(`/preview/${item.storyId.shareToken}`, { replace: true });
          return;
        }
        const tp = await api.get(`/api/templates/${item.templateId.slug}`);
        if (cancelled) return;
        setTemplate(tp);
        setValues(buildInitialValues(tp, lang));
        // The variant is fixed by the paid slot — no picker shown.
        setSelectedSku(item.variantSku || tp.variants?.find(v => v.isActive)?.sku || '');
      } catch {
        if (!cancelled) setTemplate(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderItemMode, orderId, itemIndex, lang, navigate]);

  // Key of the required field the submit guard stopped on, so the form can flag
  // it visually instead of only printing an error at the top of a long page.
  const [invalidField, setInvalidField] = useState('');

  const update = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));
  const toggleValue = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setInvalidField((prev) => (prev === key ? '' : prev));
  };

  const selectedVariant = template?.variants?.find(v => v.sku === selectedSku);

  const [error, setError] = useState('');
  // Distinct from `error` because the cap message carries a WhatsApp link (JSX),
  // not a plain string. See the render block below.
  const [blocked, setBlocked] = useState(false);
  // The visitor's existing (unpaid) preview token, returned by /can-create when
  // blocked — lets the limit message link straight to "order your story".
  const [blockedShareToken, setBlockedShareToken] = useState(null);

  // Pre-preview contact-capture step. After the story form is submitted (and the
  // optional no-photo prompt resolved), we collect the requester's name/email/
  // phone BEFORE generating — so a lead is captured even if they never buy. The
  // generation request only fires once this step is completed.
  const [showContactStep, setShowContactStep] = useState(false);
  // True while the per-IP cap pre-check runs between the form and the contact
  // step — disables the submit button so it isn't double-fired.
  const [advancing, setAdvancing] = useState(false);
  // Prefilled from a prior visit (localStorage) so returning visitors don't
  // retype. Saved again on submit and reused to prefill checkout.
  const [contact, setContact] = useState(() => {
    const saved = loadLeadContact();
    return { name: saved?.name || '', email: saved?.email || '', phone: saved?.phone || '' };
  });
  const [contactError, setContactError] = useState('');
  const updateContact = (key) => (e) =>
    setContact((prev) => ({ ...prev, [key]: e.target.value }));

  // Admin-only "create for a customer" mode: generate a preview on a customer's
  // behalf (WhatsApp / off-platform order). The contact step becomes the
  // customer's details and the server creates a real, manageable order. Persisted
  // so an admin handling several WhatsApp orders doesn't re-toggle per template.
  const { isStaff } = useAuth();
  const [forCustomer, setForCustomer] = useState(
    () => localStorage.getItem('iamhero_admin_for_customer') === '1',
  );
  const toggleForCustomer = (on) => {
    setForCustomer(on);
    if (on) localStorage.setItem('iamhero_admin_for_customer', '1');
    else localStorage.removeItem('iamhero_admin_for_customer');
  };
  // Only staff (admins + editors) can actually use customer mode; ignore a stale
  // flag otherwise.
  const customerMode = isStaff && forCustomer;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setBlocked(false);
    setInvalidField('');
    // Topic-picker fields aren't native inputs, so the browser's `required`
    // can't catch an empty selection — guard it here.
    const topicField = (template?.personalizationSchema?.fields || [])
      .find((f) => f.type === 'topic' && f.required);
    if (topicField && !String(values[topicField.key] || '').trim()) {
      setError(t.topicRequired);
      return;
    }
    // Same for toggle fields (gender). They start unselected — see
    // buildInitialValues — so the browser can't enforce them either. Stopping
    // here is what guarantees the gender in the story is one the parent chose.
    const toggleField = (template?.personalizationSchema?.fields || []).find(
      (f) => f.type === 'toggle' && f.required && !String(values[f.key] || '').trim(),
    );
    if (toggleField) {
      setInvalidField(toggleField.key);
      setError(`${t.toggleRequiredPrefix} ${lf(toggleField, 'labelAr')}`);
      document
        .getElementById(`field-${toggleField.key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // A story is far more appealing when the child sees themselves as the hero.
    // If no character photo AND no written appearance description was provided,
    // surface the "no photo" prompt (upload on the spot, or describe in words)
    // instead of generating a generic-looking child. The parent can still
    // continue without either — it's a recommendation, not a hard requirement.
    const hasCharacterPhoto = !!characterImage;
    const hasDescription = !!String(values.appearanceDescription || '').trim();
    if (!hasCharacterPhoto && !hasDescription) {
      setShowNoPhotoPrompt(true);
      return;
    }
    advanceToContact();
  };

  // Step 1 → contact step. Split out from handleSubmit so the "no photo" prompt
  // can invoke it once the parent chooses to continue (with a photo, a
  // description, or neither). Runs the per-IP free-preview cap pre-check FIRST so
  // a blocked visitor isn't made to fill the contact form only to be refused.
  // /create enforces the cap authoritatively; a failed pre-check (offline/
  // transient) is non-blocking — we let them through and the server decides.
  const advanceToContact = async () => {
    setShowNoPhotoPrompt(false);
    setError('');
    setBlocked(false);
    // Pay-first order-item mode: the slot is already paid for and we already know
    // the customer (their account) — there's no free-preview cap and no lead to
    // capture, so generate straight away.
    if (orderItemMode) {
      submitGeneration();
      return;
    }
    setAdvancing(true);
    try {
      const gate = await api.get('/api/stories/can-create');
      if (gate && gate.allowed === false) {
        setBlocked(true);
        setBlockedShareToken(gate.shareToken || null);
        setAdvancing(false);
        return;
      }
    } catch { /* ignore — server-side cap is the real boundary */ }
    setAdvancing(false);
    setShowContactStep(true);
  };

  // Contact step → generation. Validates the captured contact, persists it for
  // checkout prefill (and sends it to the server for lead capture), then fires
  // the generation request and navigates to the preview.
  const submitGeneration = () => {
    // Storefront mode captures + validates the requester's contact for lead
    // capture. Order-item mode is post-payment (account known) — no contact step.
    let name = '';
    let email = '';
    let phone = '';
    // Mint the share token up front so it can double as the CompleteRegistration
    // event id (browser⇄CAPI dedup) AND the story key we send below. We navigate
    // to the preview immediately and poll there — generation can take a couple of
    // minutes, and iOS Safari suspends a backgrounded tab and silently drops this
    // long request, so we must never depend on its response arriving.
    const shareToken = newShareToken();
    if (!orderItemMode) {
      name = contact.name.trim();
      email = contact.email.trim();
      phone = contact.phone.trim();
      if (!name || !email || !phone) { setContactError(t.contactErrorRequired); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setContactError(t.contactErrorEmail); return; }
      // Lenient phone check — enough digits to be a real number; accepts +, spaces,
      // dashes and local/international Israeli formats without over-policing.
      if (phone.replace(/\D/g, '').length < 7) { setContactError(t.contactErrorPhone); return; }
      // Stash for same-device checkout prefill (the server also stores it on the
      // story for lead capture — see /create).
      saveLeadContact({ name, email, phone });
      // The preview info is now filled and validated — this is the real lead
      // moment (name+email+phone captured), so fire Meta's CompleteRegistration
      // here. It's the event the prospecting campaign optimizes on, so keeping it
      // firing on every genuine preview-start is what teaches Meta to find more
      // preview-starters. Keyed by the unique shareToken so a double-submit (or a
      // future server-side CAPI copy) dedupes to a single conversion. Admin/staff
      // previews don't reach Meta — the pixel is suppressed while signed in
      // (see lib/pixel setPixelSuppressed), and order-item mode skips this branch.
      trackCompleteRegistration({ contentName: slug, eventId: shareToken });
    } else {
      trackEvent('payfirst_create_story', { slug: template?.slug, name: template?.nameAr });
    }
    setContactError('');

    setSubmitting(true);
    // The story is saved server-side under `shareToken` (minted above); we send it
    // along so the preview page we navigate to can poll this exact record.
    const fd = new FormData();
    // In order-item mode the template + variant are fixed by the paid slot, so the
    // server derives them from the order item; the storefront flow sends them.
    if (!orderItemMode) {
      fd.append('templateSlug', slug);
      fd.append('variantSku', selectedSku);
    }
    // Attach the per-reference descriptions (aligned to referenceImages order)
    // so generation knows what each extra image is and only uses it where it fits.
    fd.append('personalization', JSON.stringify({
      ...values,
      referenceImageDescriptions: referenceDescriptions.slice(0, referenceImages.length),
    }));
    fd.append('shareToken', shareToken);
    if (!orderItemMode) {
      fd.append('contactName', name);
      fd.append('contactEmail', email);
      fd.append('contactPhone', phone);
      // First-party Meta cookies + the page URL so the server can send the deduped
      // CompleteRegistration via the Conversions API (see metaCapi.sendRegistration)
      // with good match quality — its event_id is this same shareToken.
      const { fbp, fbc } = getFbCookies();
      if (fbp) fd.append('fbp', fbp);
      if (fbc) fd.append('fbc', fbc);
      fd.append('eventSourceUrl', window.location.href);
    }
    // Attribute this preview to the browsing visitor for the admin funnel.
    const visitorId = getVisitorId();
    if (visitorId) fd.append('visitorId', visitorId);
    // Admin only — the server re-checks the role before honouring this.
    if (!orderItemMode && customerMode) fd.append('forCustomer', 'true');
    // ?af=pdf funnel: the server freezes its campaign margin onto the story so
    // checkout charges the same price shown here (see services/afPricing).
    if (!orderItemMode && pdfOnly) fd.append('af', 'pdf');
    photos.forEach((f) => fd.append('photos', f));
    if (characterImage) fd.append('characterImage', characterImage);
    referenceImages.forEach((f) => fd.append('referenceImages', f));
    // Fire-and-forget: an SPA route change does NOT abort an in-flight fetch, so
    // the request keeps running after we navigate. The preview page polls until
    // the story is preview_ready (or the server marks it failed), so we don't
    // await the slow pipeline here. Refresh the cart badge once it lands.
    //
    // BUT record a hard failure of this POST. If it never reaches the server
    // (connection refused, server down/redeploying) the story is never created,
    // so the preview page would otherwise poll a non-existent story until the
    // 7-min timeout. The preview reads this marker and fails fast — but only when
    // it ALSO sees a 404, which is airtight: a request that did reach the server
    // writes the story stub immediately, so it would not 404. Keyed by the
    // (unique) share token; sessionStorage clears with the tab.
    try { sessionStorage.removeItem(`iamhero_create_failed:${shareToken}`); } catch { /* private mode */ }
    const endpoint = orderItemMode
      ? `/api/stories/order-item/${orderId}/${itemIndex}/create`
      : '/api/stories/create';
    api.postForm(endpoint, fd)
      .then(() => { if (!orderItemMode) refreshPendingOrders(); })
      .catch(() => {
        try { sessionStorage.setItem(`iamhero_create_failed:${shareToken}`, '1'); } catch { /* private mode */ }
      });
    // Note: the Meta "AddToCart" event is intentionally NOT fired here. Generating
    // a story is too early/noisy a signal (every guest + every failed generation
    // would count). It fires at checkout submit instead — once the buyer has
    // entered their full address and is being sent to payment (see Checkout.jsx).
    navigate(`/preview/${shareToken}`, { state: { generating: true } });
  };

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!template) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );
  if (submitting) return <div className="create-page create-page-loading"><StoryLoader /></div>;

  // Pre-preview contact-capture step. Replaces the form once the story details
  // are submitted and the cap pre-check passes; "back" returns to the form with
  // every field intact (the form state is untouched while this is shown).
  if (showContactStep) {
    return (
      <div className="create-page create-contact-step">
        <h1 className="page-title">{customerMode ? t.adminCustomerStepTitle : t.contactStepTitle}</h1>
        <p className="contact-step-subtitle">{customerMode ? t.adminCustomerStepSubtitle : t.contactStepSubtitle}</p>
        {/* Last look at who the story is about before we generate. The hero's
            gender drives every image and every verb in the text, and a wrong one
            is only discovered on the finished preview — so it gets one explicit
            read-back here, with a one-tap way back to the form. */}
        {(values.name || values.gender) && (
          <div className="hero-recap">
            <span className="hero-recap-label">{t.heroRecapLabel}</span>
            <span className="hero-recap-value">
              {[
                values.name,
                lang === 'he' ? (TOGGLE_DISPLAY_HE[values.gender] || values.gender) : values.gender,
                values.age && formatAge(values.age, t),
              ].filter(Boolean).join(' · ')}
            </span>
            <button
              type="button"
              className="link-btn hero-recap-edit"
              onClick={() => setShowContactStep(false)}
            >
              {t.heroRecapEdit}
            </button>
          </div>
        )}
        {contactError && <p className="auth-error">{contactError}</p>}
        <form
          className="story-form"
          onSubmit={(e) => { e.preventDefault(); submitGeneration(); }}
        >
          <div className="form-zone">
            <label className="field">
              <span className="field-label">{t.contactNameLabel}<span className="field-required"> *</span></span>
              <input
                type="text"
                name="name"
                autoComplete="name"
                value={contact.name}
                onChange={updateContact('name')}
                required
              />
            </label>

            <label className="field">
              <span className="field-label">{t.contactEmailLabel}<span className="field-required"> *</span></span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={contact.email}
                onChange={updateContact('email')}
                required
              />
            </label>

            <label className="field">
              <span className="field-label">{t.contactPhoneLabel}<span className="field-required"> *</span></span>
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                value={contact.phone}
                onChange={updateContact('phone')}
                required
              />
              <span className="field-helper">{t.contactPhoneHelper}</span>
            </label>
          </div>

          <button type="submit" className="submit-btn">{t.contactContinue}</button>
          <button
            type="button"
            className="link-btn create-contact-back"
            onClick={() => setShowContactStep(false)}
          >
            {t.contactBack}
          </button>
        </form>
      </div>
    );
  }

  const fields = (template.personalizationSchema?.fields || []).filter((f) => f.type !== 'hidden');
  // The therapeutic flow fields (when it happens / how they behave / desired
  // outcome) only make sense for the fears line. preset 'therapy' is the one
  // that maps to the fears story line server-side (storyLineForTemplate).
  const isFears = (template.generationConfig?.preset || '') === 'therapy';

  return (
    <div className="create-page">
      <BackButton />
      <h1 className="page-title">{orderItemMode ? t.payFirstOrderItemCreateTitle : t.createTitle}: {lf(template, 'nameAr')}</h1>
      {orderItemMode && <p className="contact-step-subtitle">{t.payFirstOrderItemCreateSubtitle}</p>}
      {error && <p className="auth-error">{error}</p>}
      {blocked && (
        <div className="free-preview-limit">
          <p className="free-preview-limit-title">{t.freePreviewLimitTitle}</p>
          <p>{t.freePreviewLimitBody}</p>
          {blockedShareToken && (
            <Link to={`/preview/${blockedShareToken}`} className="submit-btn">
              {t.freePreviewLimitOrderCta}
            </Link>
          )}
          <p>
            {t.freePreviewLimitContactPrefix}{' '}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              {t.freePreviewLimitContactLink}
            </a>{' '}
            <bdi dir="ltr">{WHATSAPP_DISPLAY}</bdi>
          </p>
        </div>
      )}

      <form className="story-form" onSubmit={handleSubmit}>
        {/* Staff-only: create this preview on a customer's behalf (WhatsApp /
            off-platform order). When on, the contact step collects the
            customer's details and the server saves a real, manageable order.
            Hidden in the post-payment order-item flow — there the order + book
            type are already fixed. */}
        {isStaff && !orderItemMode && (
          <div className="admin-create-banner">
            <label className="admin-create-toggle">
              <input
                type="checkbox"
                checked={forCustomer}
                onChange={(e) => toggleForCustomer(e.target.checked)}
              />
              <span>{t.adminForCustomerToggle}</span>
            </label>
            <p className="admin-create-banner-hint">
              {customerMode ? t.adminForCustomerOn : t.adminForCustomerOff}
            </p>
          </div>
        )}

        <div className="form-zone">
          {lang !== 'he' && (
            <div className="field field-story-language">
              <span className="field-label">{t.storyLanguageLabel}</span>
              <div className="gender-toggle">
                <button
                  type="button"
                  className={`gender-btn ${values.storyLanguage === 'fos7a' ? 'active' : ''}`}
                  onClick={() => toggleValue('storyLanguage', 'fos7a')}
                >
                  {t.storyLanguageFos7a}
                </button>
                <button
                  type="button"
                  className={`gender-btn ${values.storyLanguage === 'lahja' ? 'active' : ''}`}
                  onClick={() => toggleValue('storyLanguage', 'lahja')}
                >
                  {t.storyLanguageLahja}
                </button>
                {/* Staff-only: generate the plot in English (same prompt, the
                    finished MSA draft is translated to English). Off-catalog,
                    for customers who want an English book. */}
                {customerMode && (
                  <button
                    type="button"
                    className={`gender-btn ${values.storyLanguage === 'en' ? 'active' : ''}`}
                    onClick={() => toggleValue('storyLanguage', 'en')}
                  >
                    {t.storyLanguageEnglish}
                  </button>
                )}
              </div>
              <span className="field-helper">{t.storyLanguageHelper}</span>
            </div>
          )}
          <div className="field field-image-style">
            <span className="field-label">{t.imageStyleLabel}</span>
            <div className="gender-toggle">
              {t.imageStyleOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`gender-btn ${values.imageStyle === opt.value ? 'active' : ''}`}
                  onClick={() => toggleValue('imageStyle', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="field-helper">{t.imageStyleHelper}</span>
          </div>
        </div>

        <div className="form-zone">
          {fields.map((field) => (
            <div key={field.key} className="field" id={`field-${field.key}`}>
              <span className="field-label">
                {lf(field, 'labelAr')}
                {field.required && <span className="field-required"> *</span>}
              </span>

              {field.type === 'topic' ? (
                <TopicPicker
                  field={field}
                  value={values[field.key] || ''}
                  other={topicOther}
                  collapsed={topicLocked}
                  onSelect={(val) => { setTopicOther(false); setTopicLocked(false); toggleValue(field.key, val); }}
                  onOther={() => { setTopicOther(true); setTopicLocked(false); toggleValue(field.key, ''); }}
                  onOtherChange={update(field.key)}
                  onEdit={() => setTopicLocked(false)}
                />
              ) : field.type === 'toggle' && field.options ? (
                <>
                  <div className={`gender-toggle ${invalidField === field.key ? 'invalid' : ''}`}>
                    {field.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`gender-btn ${values[field.key] === opt ? 'active' : ''}`}
                        onClick={() => toggleValue(field.key, opt)}
                      >
                        {lang === 'he' ? (TOGGLE_DISPLAY_HE[opt] || opt) : opt}
                      </button>
                    ))}
                  </div>
                  {/* Nothing is pre-selected, so say out loud that an answer is
                      still missing — the toggle alone reads as "answered". */}
                  {field.required && !String(values[field.key] || '').trim() && (
                    <span className="field-choose-hint">{t.toggleChooseHint}</span>
                  )}
                </>
              ) : field.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={values[field.key] || ''}
                  onChange={update(field.key)}
                  required={field.required}
                />
              ) : field.type === 'select' ? (
                <select
                  value={values[field.key] || ''}
                  onChange={update(field.key)}
                  required={field.required}
                >
                  <option value="">--</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : field.type === 'photo' ? (
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, field.max || 5))}
                />
              ) : (
                <input
                  type="text"
                  value={values[field.key] || ''}
                  onChange={update(field.key)}
                  required={field.required}
                />
              )}

              {lf(field, 'helpAr') && !(field.type === 'topic' && topicLocked) && (
                <span className="field-helper">{lf(field, 'helpAr')}</span>
              )}
            </div>
          ))}
        </div>

        {/* Fears-only flow fields. The fears prompt has dedicated slots for the
            trigger situation, the child's current reaction, and the desired
            outcome; collect them here so the paid path matches the dev form. */}
        {isFears && (
          <div className="form-zone">
            <label className="field">
              <span className="field-label">{t.whenHappens}</span>
              <input
                type="text"
                value={values.whenHappens || ''}
                onChange={update('whenHappens')}
                placeholder={t.whenHappensPlaceholder}
              />
            </label>

            <label className="field">
              <span className="field-label">{t.howBehaves}</span>
              <input
                type="text"
                value={values.howBehaves || ''}
                onChange={update('howBehaves')}
                placeholder={t.howBehavesPlaceholder}
              />
            </label>

            <label className="field">
              <span className="field-label">{t.smallGoal}</span>
              <input
                type="text"
                value={values.smallGoal || ''}
                onChange={update('smallGoal')}
                placeholder={t.smallGoalPlaceholder}
              />
              <span className="field-helper">{t.smallGoalHelper}</span>
            </label>
          </div>
        )}

        {/* Global therapeutic-detail fields — same across every template so the
            one-prompt story line always receives the same shape of input. */}
        <div className="form-zone">
          <PersonalityTraitsField
            value={values.personalityTraits}
            onChange={(val) => toggleValue('personalityTraits', val)}
          />
          <FamilyStructureField
            value={values.familyStructure}
            onChange={(val) => toggleValue('familyStructure', val)}
          />
        </div>

        <div className="form-zone">
          <div className="image-section-head">
            <span className="zone-title">{t.imageSectionTitle}</span>
            <ImageTips />
          </div>

          <div className="field">
            <span className="field-label">{t.characterImageLabel}</span>
            <ImageDropzone
              files={characterImage}
              onChange={setCharacterImage}
              multiple={false}
              max={1}
              onInterceptOpen={() => { setShowPhotoHelp(true); return true; }}
            />
            <span className="field-helper">{t.characterImageHelp}</span>
          </div>

          {/* Reference images (a pet, a friend, a place) are a staff-only tool —
              admins building a story for a customer can still attach them. Regular
              customers no longer see this field; only the child photo above. */}
          {isStaff && (
            <div className="field">
              <span className="field-label">{t.referenceImagesLabel}</span>
              <ImageDropzone
                files={referenceImages}
                onChange={setReferenceImages}
                multiple
                max={10}
                descriptions={referenceDescriptions}
                onDescriptionsChange={setReferenceDescriptions}
                descriptionPlaceholder={t.referenceDescriptionPlaceholder}
              />
              <span className="field-helper">{t.referenceImagesHelp}</span>
              <span className="field-helper">{t.referenceDescriptionHelp}</span>
            </div>
          )}
        </div>

        {/* In the ?af=pdf funnel the variant is fixed — show what they're
            getting and the price instead of a choice. In the pay-first order-item
            flow the book is already bought, so no variant picker or price shows. */}
        {!orderItemMode && (
          <div className="form-zone">
            {!pdfOnly && (
              <VariantPicker
                variants={template.variants}
                selected={selectedSku}
                onSelect={setSelectedSku}
              />
            )}
            {selectedVariant && (
              <p className="template-detail-price">
                {pdfOnly ? `${t.digital} — ` : ''}
                {/* af=pdf shows the campaign price (official + margin) — exactly
                    what the server will freeze onto the story and charge. */}
                {((selectedVariant.priceCents + (pdfOnly ? (template.afPdfMarginCents || 0) : 0)) / 100).toFixed(0)}{t.currency}
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          className="submit-btn"
          disabled={submitting || advancing}
        >
          {orderItemMode ? t.createYourStory : (advancing ? t.generatingPreview : t.generatePreview)}
        </button>
      </form>

      {/* Tapping the empty character-photo zone lands here first: the worked
          example, with the upload right under it so the parent never leaves the
          modal to act on what they just learned. */}
      {showPhotoHelp && createPortal((
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t.photoHelpTitle}
          onClick={() => setShowPhotoHelp(false)}
        >
          <div className="modal-card photo-help-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              aria-label={t.photoGuideClose}
              onClick={() => setShowPhotoHelp(false)}
            >
              ×
            </button>
            <h3 className="modal-title">{t.photoHelpTitle}</h3>
            <p className="modal-text">{t.photoHelpText}</p>

            <PhotoGuide />

            <div className="field">
              <span className="field-label">{t.photoHelpUploadLabel}</span>
              <ImageDropzone
                files={characterImage}
                onChange={setCharacterImage}
                multiple={false}
                max={1}
              />
            </div>

            <button type="button" className="submit-btn" onClick={() => setShowPhotoHelp(false)}>
              {characterImage ? t.photoHelpDone : t.photoHelpLater}
            </button>
          </div>
        </div>
      ), document.body)}

      {showNoPhotoPrompt && createPortal((
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t.noPhotoTitle}
          onClick={() => setShowNoPhotoPrompt(false)}
        >
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              aria-label={t.noPhotoClose}
              onClick={() => setShowNoPhotoPrompt(false)}
            >
              ×
            </button>
            <h3 className="modal-title">{t.noPhotoTitle}</h3>
            <p className="modal-text">{t.noPhotoMessage}</p>

            <div className="field">
              <span className="field-label">{t.noPhotoUploadLabel}</span>
              <ImageDropzone
                files={characterImage}
                onChange={setCharacterImage}
                multiple={false}
                max={1}
              />
            </div>

            <div className="modal-or"><span>{t.noPhotoOr}</span></div>

            <div className="field">
              <span className="field-label">{t.noPhotoDescribeLabel}</span>
              <textarea
                rows={3}
                value={values.appearanceDescription || ''}
                onChange={update('appearanceDescription')}
                placeholder={t.noPhotoDescribePlaceholder}
              />
              <span className="field-helper">{t.noPhotoDescribeHelp}</span>
            </div>

            <button type="button" className="submit-btn" onClick={advanceToContact}>
              {characterImage || String(values.appearanceDescription || '').trim()
                ? t.noPhotoContinue
                : t.noPhotoContinueAnyway}
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
